import React, { useRef, useEffect, useState } from 'react';

// Performance: Throttle slider updates
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

export default function SliderControl({
  scaleFactor,
  minScale,
  maxScale,
  sliderHeight,
  sliderThumbHeight,
  onScaleChange
}) {
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startThumbBottom, setStartThumbBottom] = useState(0);
  const rafRef = useRef(null);
  
  // Throttled scale change for smooth performance
  const throttledScaleChange = useRef(
    throttle((newScale) => {
      onScaleChange(newScale);
    }, 16) // ~60fps
  ).current;

  useEffect(() => {
    if (!thumbRef.current) return;
    const sliderValue = (scaleFactor - minScale) / (maxScale - minScale);
    const thumbBottom = sliderValue * (sliderHeight - sliderThumbHeight);
    thumbRef.current.style.bottom = thumbBottom + 'px';
  }, [scaleFactor, minScale, maxScale, sliderHeight, sliderThumbHeight]);

  const onMouseDownThumb = (e) => {
    setIsDragging(true);
    setStartY(e.clientY);
    setStartThumbBottom(parseInt(thumbRef.current.style.bottom) || 0);
    thumbRef.current.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    e.stopPropagation();
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    
    // Use requestAnimationFrame for smooth updates
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    
    rafRef.current = requestAnimationFrame(() => {
      const deltaY = startY - e.clientY;
      let newBottom = startThumbBottom + deltaY;
      newBottom = Math.max(0, Math.min(newBottom, sliderHeight - sliderThumbHeight));
      thumbRef.current.style.bottom = newBottom + 'px';

      const sliderValue = newBottom / (sliderHeight - sliderThumbHeight);
      const newScale = minScale + sliderValue * (maxScale - minScale);
      throttledScaleChange(newScale);
    });
  };

  const onMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      thumbRef.current.style.cursor = 'grab';
      document.body.style.userSelect = '';
    }
  };

  const onSliderClick = (e) => {
    if (e.target !== thumbRef.current) {
      // Smooth transition for click jumps
      thumbRef.current.style.transition = 'bottom 0.2s ease-out';
      
      const rect = sliderRef.current.getBoundingClientRect();
      let clickY = rect.bottom - e.clientY;
      clickY = Math.max(0, Math.min(clickY, sliderHeight - sliderThumbHeight));
      thumbRef.current.style.bottom = clickY + 'px';

      const sliderValue = clickY / (sliderHeight - sliderThumbHeight);
      const newScale = minScale + sliderValue * (maxScale - minScale);
      throttledScaleChange(newScale);
      
      // Remove transition after animation
      setTimeout(() => {
        if (thumbRef.current) thumbRef.current.style.transition = '';
      }, 200);
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // Cleanup RAF on unmount
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  });

  return (
    <div id="scale-slider" ref={sliderRef} onMouseDown={onSliderClick}>
      <div id="slider-thumb" ref={thumbRef} onMouseDown={onMouseDownThumb}></div>
    </div>
  );
}
